import { forwardRef, useImperativeHandle, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  useCheckCryptoOnchainPayment,
  useCryptoOnchainNetworks,
  useCryptoOnchainPayments,
  useCryptoOnchainStoreStatus,
  useEnableCryptoOnchain,
  useUpdateCryptoOnchainPayoutAddresses,
} from '@/lib/api/crypto-onchain';

import {
  CRYPTO_ONCHAIN_PAYOUT_ADDRESS_KINDS,
  EMPTY_CRYPTO_ONCHAIN_FORM_VALUES,
  buildCryptoOnchainFormSchema,
  isCryptoOnchainPayoutKindLive,
  toCryptoOnchainFormValues,
  toCryptoOnchainPayoutAddressesInput,
  type CryptoOnchainFormValues,
  type CryptoOnchainPayoutAddressKind,
} from './crypto-onchain-form.schema';
import {
  initialPicks,
  offersANetwork,
  pickerRows,
  picksChanged,
  toSelectedAssetsInput,
  type CryptoOnchainAssetPick,
} from './crypto-onchain-networks';
import { CryptoOnchainNetworksCard, type CryptoOnchainNetworksState } from './crypto-onchain-networks-card';
import { CryptoOnchainPaymentsTable } from './crypto-onchain-payments-table';
import { CryptoOnchainSandboxCard } from './crypto-onchain-sandbox-card';
import { CryptoOnchainStatusCard } from './crypto-onchain-status-card';
import { CryptoOnchainTestnetAddressesCard } from './crypto-onchain-testnet-addresses-card';

/** What the header switch of IntegrationConfigView drives: submitting the form enables (or saves). */
export interface CryptoOnchainFormHandle {
  submit: () => Promise<void>;
}

type Props = {
  storeId: string;
  /** The crypto-api merchant now exists and the link is active: the header switch can show ON. */
  onEnabled: () => void;
  isLoading?: boolean;
};

const RECENT_PAYMENTS_LIMIT = 10;

/** Address prefixes, not copy: they tell the merchant which wallet goes where. */
const ADDRESS_PLACEHOLDERS: Record<CryptoOnchainPayoutAddressKind, string> = {
  evm: '0x…',
  solana: '…',
  tron: 'T…',
  btc: 'bc1…',
  ltc: 'ltc1…',
  doge: 'D…',
};

function getAddressFieldCopy(
  t: TFunction<'integrations'>,
): Record<CryptoOnchainPayoutAddressKind, { label: string; hint: string | null }> {
  return {
    evm: { label: t('cryptoOnchain.addresses.evm'), hint: t('cryptoOnchain.addresses.evmHint') },
    solana: { label: t('cryptoOnchain.addresses.solana'), hint: null },
    tron: { label: t('cryptoOnchain.addresses.tron'), hint: null },
    btc: { label: t('cryptoOnchain.addresses.btc'), hint: null },
    ltc: { label: t('cryptoOnchain.addresses.ltc'), hint: null },
    doge: { label: t('cryptoOnchain.addresses.doge'), hint: null },
  };
}

/**
 * Merchant surface of on-chain crypto payments (crypto-api) for one store.
 *
 * Unlike the other integration forms this one never calls `integrations.update`: there is nothing a
 * merchant could type that is a credential (the platform secret is an env var, the webhook secret is
 * minted server-side). Submitting the form runs `cryptoOnchain.enable` while the store is not live
 * (one shot: crypto-api merchant + webhook + integration row) and `cryptoOnchain.updatePayoutAddresses`
 * once it is. The header switch of the config view submits this same form through the ref handle, so
 * the one-click enable and the "Save configuration" button share one validation and one mutation.
 * The networks card sends the networks picked with the same call, only when its ticks changed.
 */
export const CryptoOnchainForm = forwardRef<CryptoOnchainFormHandle, Props>(function CryptoOnchainForm(
  { storeId, onEnabled, isLoading = false },
  ref,
) {
  const { t } = useTranslation('integrations');
  const status = useCryptoOnchainStoreStatus(storeId);
  const enable = useEnableCryptoOnchain();
  const updateAddresses = useUpdateCryptoOnchainPayoutAddresses();
  const isLinked = status.data !== undefined && status.data.status !== 'not_enabled';
  const payments = useCryptoOnchainPayments(storeId, RECENT_PAYMENTS_LIMIT, { enabled: isLinked });
  const checkPayment = useCheckCryptoOnchainPayment();
  const [sandboxOnEnable, setSandboxOnEnable] = useState(false);
  const networks = useCryptoOnchainNetworks(storeId);
  // An enabled store whose picks could not be read gets no picker: a save would overwrite them blind.
  const isPickUnknown = status.data?.enabled === true && status.data.selectedAssets === null;
  const networksState: CryptoOnchainNetworksState = networks.isLoading
    ? 'loading'
    : networks.isError || isPickUnknown
      ? 'unavailable'
      : 'ready';
  // The ticks the merchant changed, for the mode they were changed in; null until then.
  const [touchedPicks, setTouchedPicks] = useState<{
    isSandbox: boolean;
    picks: CryptoOnchainAssetPick;
  } | null>(null);
  const [pickError, setPickError] = useState<string | null>(null);

  const form = useForm<CryptoOnchainFormValues>({
    resolver: zodResolver(buildCryptoOnchainFormSchema(t)),
    defaultValues: EMPTY_CRYPTO_ONCHAIN_FORM_VALUES,
    // The stored addresses arrive asynchronously: `values` resets the form when they load (or change
    // after a save) without clobbering a field the merchant is editing.
    values: status.data ? toCryptoOnchainFormValues(status.data.payoutAddresses) : undefined,
    resetOptions: { keepDirtyValues: true },
  });
  const isPending = enable.isPending || updateAddresses.isPending || isLoading;
  const isSandbox = status.data?.enabled ? status.data.sandbox === true : sandboxOnEnable;
  const testnetWallets = status.data?.testnetPayoutAddresses ?? {};
  const catalog = networks.data ?? [];
  const startingPicks = initialPicks(catalog, isSandbox, status.data?.selectedAssets);
  const picks = touchedPicks?.isSandbox === isSandbox ? touchedPicks.picks : startingPicks;
  const rows = pickerRows(catalog, isSandbox, form.watch(), testnetWallets);

  const handlePicksChange = (next: CryptoOnchainAssetPick) => {
    setTouchedPicks({ isSandbox, picks: next });
    setPickError(null);
  };

  const handleFormSubmit = async (values: CryptoOnchainFormValues) => {
    if (enable.isPending || updateAddresses.isPending) return;
    const isCatalogReady = networksState === 'ready';
    if (isCatalogReady && !offersANetwork(pickerRows(catalog, isSandbox, values, testnetWallets), picks)) {
      setPickError(t('cryptoOnchain.networks.required'));
      return;
    }
    const payoutAddresses = toCryptoOnchainPayoutAddressesInput(values);
    const selectedAssets = isCatalogReady ? toSelectedAssetsInput(picks, startingPicks, isSandbox) : null;
    const picksInput = selectedAssets ? { selectedAssets } : {};
    try {
      if (status.data?.enabled) {
        const next = await updateAddresses.mutateAsync({ storeId, payoutAddresses, ...picksInput });
        form.reset(toCryptoOnchainFormValues(next.payoutAddresses));
        setTouchedPicks(null);
        // The save also puts back an integration row that was deleted while the link stayed live,
        // so the header switch, which reads that row, shows ON again.
        onEnabled();
        return;
      }
      const next = await enable.mutateAsync({
        storeId,
        payoutAddresses,
        sandbox: sandboxOnEnable,
        ...picksInput,
      });
      form.reset(toCryptoOnchainFormValues(next.payoutAddresses));
      setTouchedPicks(null);
      onEnabled();
    } catch {
      // The mutation hook already showed the translated toast; the form keeps what was typed.
    }
  };

  useImperativeHandle(ref, () => ({ submit: () => form.handleSubmit(handleFormSubmit)() }));

  // One check at a time: the button of the row in flight waits, the others stay clickable afterwards.
  const handleCheckPayment = (cryptoOnchainPaymentId: string) => {
    if (checkPayment.isPending) return;
    checkPayment.mutate({ storeId, cryptoOnchainPaymentId });
  };
  const checkingPaymentId = checkPayment.isPending
    ? (checkPayment.variables?.cryptoOnchainPaymentId ?? null)
    : null;

  const fieldCopy = getAddressFieldCopy(t);
  const { errors } = form.formState;
  const fields = CRYPTO_ONCHAIN_PAYOUT_ADDRESS_KINDS.map((kind) => ({
    kind,
    inputId: `crypto-onchain-${kind}`,
    label: fieldCopy[kind].label,
    hint: fieldCopy[kind].hint,
    placeholder: ADDRESS_PLACEHOLDERS[kind],
    error: errors[kind]?.message,
    isRecommended: kind === 'evm',
    isComingSoon: !isCryptoOnchainPayoutKindLive(kind),
  }));

  return (
    <>
      <form id="integration-config-form" onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-6">
        <CryptoOnchainStatusCard
          status={status.data}
          isLoading={status.isLoading}
          isError={status.isError}
          isPending={isPending}
        />

        <CryptoOnchainSandboxCard
          storeId={storeId}
          status={status.data}
          sandboxOnEnable={sandboxOnEnable}
          onSandboxOnEnableChange={setSandboxOnEnable}
          isPending={isPending}
        />

        {status.data?.enabled === true && status.data.sandbox === true && (
          <CryptoOnchainTestnetAddressesCard
            key={JSON.stringify(status.data.testnetPayoutAddresses)}
            storeId={storeId}
            status={status.data}
          />
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('cryptoOnchain.addresses.title')}</CardTitle>
            <CardDescription>{t('cryptoOnchain.addresses.description')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {fields.map((field) => (
              <div key={field.kind} className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label htmlFor={field.inputId}>{field.label}</Label>
                  {field.isRecommended && (
                    <Badge variant="outline" className="text-xs">
                      {t('cryptoOnchain.addresses.recommended')}
                    </Badge>
                  )}
                  {field.isComingSoon && (
                    <Badge variant="secondary" className="text-xs">
                      {t('cryptoOnchain.addresses.comingSoon')}
                    </Badge>
                  )}
                </div>
                <Input
                  id={field.inputId}
                  placeholder={field.placeholder}
                  disabled={isPending || field.isComingSoon}
                  autoComplete="off"
                  spellCheck={false}
                  className="font-mono text-sm"
                  {...form.register(field.kind)}
                />
                {field.hint && <p className="text-muted-foreground text-xs">{field.hint}</p>}
                {field.error && <p className="text-destructive text-xs">{field.error}</p>}
              </div>
            ))}
          </CardContent>
        </Card>

        <CryptoOnchainNetworksCard
          state={networksState}
          rows={rows}
          picks={picks}
          error={pickError}
          isPending={isPending}
          onChange={handlePicksChange}
        />
      </form>

      <CryptoOnchainPaymentsTable
        payments={payments.data?.items ?? []}
        isLoading={isLinked && payments.isLoading}
        onCheckPayment={handleCheckPayment}
        checkingPaymentId={checkingPaymentId}
      />
    </>
  );
});
